import {onScopeDispose, reactive, watch} from 'vue';

export type NotificationOptions = {
    duration?: number;
    dedupe?: boolean;
};

export type NotificationMessage = {
    id: number;
    message: string;
    duration: number;
};

export type NotificationState = {
    warnings: NotificationMessage[];
    infos: NotificationMessage[];
};

export type NotificationPublisher = {
    warning(message: string | string[], options?: NotificationOptions): void;
    warningError(error: unknown, fallback: string, options?: NotificationOptions): void;
    info(message: string | string[], options?: NotificationOptions): void;
};

export type NotificationCenter = NotificationPublisher & {
    state: NotificationState;
    messages(): {warnings: string[]; infos: string[]};
    clear(): void;
};

/** Creates an isolated queue for tests and headless operations that collect their own diagnostics. */
export function createNotificationCenter(): NotificationCenter {
    const state = reactive<NotificationState>({warnings: [], infos: []});
    let nextId = 1;

    function publish(key: keyof NotificationState, value: string | string[], options: NotificationOptions = {}) {
        const messages = Array.isArray(value) ? value : [value];
        for (const message of messages) {
            if (!message || (options.dedupe && state[key].some((item) => item.message === message))) continue;
            state[key].push({id: nextId++, message, duration: options.duration ?? 3000});
        }
    }

    return {
        state,
        warning: (message, options) => publish('warnings', message, options),
        warningError: (error, fallback, options) =>
            publish('warnings', error instanceof Error ? error.message : fallback, options),
        info: (message, options) => publish('infos', message, options),
        messages: () => ({
            warnings: state.warnings.map(({message}) => message),
            infos: state.infos.map(({message}) => message),
        }),
        clear: () => {
            state.warnings.splice(0);
            state.infos.splice(0);
        },
    };
}

const notifications = createNotificationCenter();

/** Returns the application-wide queue rendered by App.vue. Editor internals publish through their owning Session. */
export function useNotifications(): NotificationCenter {
    return notifications;
}

/** The root application owns the only timers that consume visible notifications. */
export function useNotificationExpiry(state: NotificationState): void {
    for (const key of ['warnings', 'infos'] as const) {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const stop = watch(
            () => state[key][0]?.id,
            () => {
                clearTimeout(timer);
                const next = state[key][0];
                if (!next) return;
                timer = setTimeout(() => state[key].shift(), next.duration);
            },
            {immediate: true},
        );
        onScopeDispose(() => {
            stop();
            clearTimeout(timer);
        });
    }
}
